import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { T, useTranslation } from "@redotech/redo-i18n";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  ReturnsSettingsExperience,
  useReturnsSettingsExperience,
} from "@redotech/redo-merchant-app-common/settings/use-returns-settings-experience";
import {
  RETURNS_SETTINGS_TAB_BY_LEGACY_PATH,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
} from "@redotech/redo-settings-common/flat-team-settings/returns/returns-settings-tab";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { PlatformEvents } from "../../../platform-events";
import { formatOldSettingsShutoffDate } from "../../../setting/returns/switch-back/old-settings-shutoff";
import { SwitchBackModal } from "../../../setting/returns/switch-back/switch-back-modal";
import { useSwitchToLegacySettings } from "../../../setting/returns/switch-back/use-switch-to-legacy-settings";
import {
  bannerLead,
  ExperienceBannerLink,
  ExperienceBannerShell,
} from "./experience-banner-shell";

const DISMISSED_STORAGE_KEY =
  "redo.returns-settings.switch-back-banner-dismissed";

/** Offers the way back to the old pages on the new one, and the way forward on the old ones. */
export const ReturnsSettingsExperienceBanner = memo(
  function ReturnsSettingsExperienceBanner() {
    const experience = useReturnsSettingsExperience();
    if (experience.pending || !experience.canSwitch) {
      return null;
    }
    return experience.useNewUi ? (
      <SwitchBackBanner />
    ) : (
      <SwitchToNewBanner experience={experience} />
    );
  },
);

const SwitchBackBanner = memo(function SwitchBackBanner() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useLocalStorage(
    DISMISSED_STORAGE_KEY,
    false,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const switchToLegacy = useSwitchToLegacySettings();

  const switchBack = useHandler(() => {
    setModalOpen(false);
    switchToLegacy();
  });

  const dismiss = useHandler(() => {
    setDismissed(true);
    amplitude.track(
      PlatformEvents.RETURNS_SETTINGS_SWITCH_BACK_BANNER_DISMISSED,
    );
  });

  if (
    dismissed ||
    pathname !== routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH)
  ) {
    return null;
  }

  return (
    <>
      <ExperienceBannerShell
        action={
          <RedoButton
            aria-label={t("Dismiss", { context: "notification banner" })}
            IconLeading={CloseIcon}
            onClick={dismiss}
            size="sm"
          />
        }
      >
        <T
          components={{
            ...emphasis,
            cta: (
              <ExperienceBannerLink
                dataTrackId="merchant.settings.returns.switch-back.banner-link"
                onClick={() => setModalOpen(true)}
                text={t("switch back", {
                  context: "link to return to the old returns settings",
                })}
              />
            ),
          }}
          i18nKey="<lead>We've updated returns settings.</lead> You can <cta/> to the previous experience for now. This update becomes permanent on <bold>{{date}}</bold>."
          values={{ date: formatOldSettingsShutoffDate() }}
        />
      </ExperienceBannerShell>
      <SwitchBackModal
        onCancel={() => setModalOpen(false)}
        onConfirm={switchBack}
        open={modalOpen}
      />
    </>
  );
});

/**
 * Not dismissible: after switching back it is the only way to the new page,
 * which merchants return to once they've reported what it was missing.
 */
const SwitchToNewBanner = memo(function SwitchToNewBanner({
  experience,
}: {
  experience: ReturnsSettingsExperience;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const tab = Object.entries(RETURNS_SETTINGS_TAB_BY_LEGACY_PATH).find(
    ([legacyPath]) => pathname === routes.getSettingsSubRoute(legacyPath),
  )?.[1];

  const switchToNew = useHandler(() => {
    if (!tab) {
      return;
    }
    experience.switchToNew();
    navigate(
      `${routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH)}?${new URLSearchParams({ [RETURNS_SETTINGS_TAB_QUERY_PARAM]: tab })}`,
    );
  });

  if (!tab) {
    return null;
  }

  return (
    <ExperienceBannerShell
      action={
        <RedoButton
          dataTrackId="merchant.settings.returns.switch-to-new.banner-button"
          hierarchy="secondary"
          onClick={switchToNew}
          size="sm"
          text={t("Switch to new settings")}
        />
      }
    >
      <T
        components={emphasis}
        i18nKey="<lead>You're using the previous Returns settings.</lead> You can switch to the new experience anytime. It becomes permanent on <bold>{{date}}</bold>."
        values={{ date: formatOldSettingsShutoffDate() }}
      />
    </ExperienceBannerShell>
  );
});

const emphasis = {
  lead: bannerLead,
  bold: (
    <Text as="span" fontSize="md" fontWeight="bold" textColor="primary">
      {null}
    </Text>
  ),
};
